import assignOffsetPagination from "@/schemas/assignOffsetPagination";
import { customerInfoProviderToken } from "@/tokens";
import { WishlistItemEntity } from "@apps/wishlist/entities/WishlistItemEntity";
import { WishlistsBackInStockItemEntity } from "@apps/wishlist/entities/WishlistsBackInStockItemEntity";
import { WishlistsItemsTotalCountEntity } from "@apps/wishlist/entities/WishlistsItemsTotalCountEntity";
import { WishlistWithItemsEntity } from "@apps/wishlist/entities/WishlistWithItemsEntity";
import { WishlistItemSchema } from "@apps/wishlist/schemas/WishlistItemSchema";
import { WishlistsItemsTotalCountSchema } from "@apps/wishlist/schemas/WishlistsItemsTotalCountSchema";
import { getBackInStockWishlistItemsParams } from "@apps/wishlist/services/WishlistsBackInStockItemsService";
import { wishlistAuthApiClientToken } from "@apps/wishlist/tokens";
import {
    QueryPaginationMeta,
    WISHLIST_ITEMS_SORTING_TYPE,
} from "@apps/wishlist/types";
import { JsonMimeType } from "@lib/ApiClient";
import AuthApiClient from "@lib/ApiClient/AuthApiClient";
import { CustomerInfoProviderInterface } from "@lib/CustomerInfoProvider/CustomerInfoProviderInterface";
import { I18nModule } from "@modules/i18n/I18nModule";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { create } from "superstruct";
import { inject, injectable } from "tsyringe";

import { AddApproverResponseSchema } from "./responses/AddApproverResponseSchema";
import { ApproveWishlistResponseSchema } from "./responses/ApproveWishlistResponseSchema";
import { CreateWishlistResponseSchema } from "./responses/CreateWishlistResponseSchema";
import { DeleteApproverResponseSchema } from "./responses/DeleteApproverResponseSchema";
import { DeleteItemResponseSchema } from "./responses/DeleteItemResponseSchema";
import { DeleteWishlistResponseSchema } from "./responses/DeleteWishlistResponseSchema";
import { DuplicateWishlistResponseSchema } from "./responses/DuplicateWishlistResponseSchema";
import { GetBackInStockItemsResponseSchema } from "./responses/GetBackInStockItemsResponseSchema";
import { GetWishlistsResponseSchema } from "./responses/GetWishlistsResponseSchema";
import { MoveItemResponseSchema } from "./responses/MoveItemResponseSchema";
import { ProductStatusesResponseSchema } from "./responses/ProductStatusesResponseSchema";
import { UpdateItemQuantityResponseSchema } from "./responses/UpdateItemQuantityResponseSchema";
import { UpdateItemVariantResponseSchema } from "./responses/UpdateItemVariantResponseSchema";
import { UpdateWishlistResponseSchema } from "./responses/UpdateWishlistResponseSchema";

export type GetWishlistsParams = {
    orderBy?: WISHLIST_ITEMS_SORTING_TYPE;
};

export type UpdateWishlistRequestBody = {
    wishlistId: number;
    newTitle?: string;
    isDefault?: boolean;
    isPrivate?: boolean;
};

@injectable()
export class WishlistsApiService {
    private readonly locale: string;

    public constructor(
        @inject(wishlistAuthApiClientToken)
        private readonly apiClient: AuthApiClient,
        @inject(I18nModule)
        private readonly i18nModule: I18nModule,
        @inject(customerInfoProviderToken)
        private readonly customerInfoProvider: CustomerInfoProviderInterface
    ) {
        this.locale =
            this.i18nModule.exports.localizationInfoProvider.getGwI18nLocale();
    }

    getWishlists(
        queryParams?: GetWishlistsParams
    ): Observable<WishlistWithItemsEntity[]> {
        const companyId = this.customerInfoProvider.getCompany()?.id;
        const params = {
            ...queryParams,
            locale: this.locale,
            ...(companyId && { companyId }),
        };

        return this.apiClient
            .get({
                url: "/getWishlists",
                responseType: "json",
                queryParams: params,
            })
            .pipe(
                map((response) => {
                    return create(response.body, GetWishlistsResponseSchema);
                }),
                map((result) => {
                    return result.wishlistsWithItems;
                })
            );
    }

    getWishlistItems(queryParams: QueryPaginationMeta) {
        return this.apiClient
            .get({
                url: "/getItems",
                queryParams,
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        assignOffsetPagination(WishlistItemSchema)
                    );
                })
            );
    }

    getItemsTotalCount(): Observable<WishlistsItemsTotalCountEntity> {
        const companyId = this.customerInfoProvider.getCompany()?.id;

        return this.apiClient
            .get({
                url: "/getItemsTotalCount",
                responseType: "json",
                ...(companyId && { queryParams: { companyId } }),
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        WishlistsItemsTotalCountSchema
                    );
                })
            );
    }

    mergeWishlists(saveWishlistsToken: string): Observable<unknown> {
        return this.apiClient
            .post({
                url: "/attachSavedWishlists",
                body: JSON.stringify({ saveWishlistsToken }),
                contentType: JsonMimeType,
                responseType: "text",
            })
            .pipe(
                map((result) => {
                    return result;
                })
            );
    }

    saveWishlists(
        email: string,
        isAcceptsEmailMarketing: boolean
    ): Observable<unknown> {
        return this.apiClient
            .post({
                url: "/saveWishlistItems",
                body: JSON.stringify({
                    email,
                    isAcceptsEmailMarketing,
                }),
                contentType: JsonMimeType,
                responseType: "text",
            })
            .pipe(
                map((result) => {
                    return result;
                })
            );
    }

    addItem(data: {
        wishlistId: number;
        productId: number;
        variantId: number;
    }): Observable<WishlistItemEntity> {
        return this.apiClient
            .post({
                url: "/addItem",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, WishlistItemSchema);
                }),
                map((result) => {
                    return result;
                })
            );
    }

    public deleteItem(data: {
        wishlistId: number | null;
        productId: number;
        variantId: number | null;
    }): Observable<WishlistItemEntity> {
        const companyId = this.customerInfoProvider.getCompany()?.id;

        return this.apiClient
            .delete({
                url: "/deleteItem",
                queryParams: {
                    ...(data.wishlistId
                        ? { wishlistId: data.wishlistId }
                        : undefined),
                    ...(data.variantId
                        ? { variantId: data.variantId }
                        : undefined),
                    productId: data.productId,
                    ...(companyId && { companyId }),
                },
            })
            .pipe(
                map((response) => {
                    return create(response.body, DeleteItemResponseSchema);
                }),
                map((result) => {
                    return result.deletedItem;
                })
            );
    }

    public moveItem(data: {
        wishlistId: number;
        productId: number;
        variantId: number | null;
        targetWishlistId: number;
    }): Observable<WishlistItemEntity> {
        const companyId = this.customerInfoProvider.getCompany()?.id;

        const requestBody = data.variantId
            ? {
                  wishlistId: data.wishlistId,
                  productId: data.productId,
                  variantId: data.variantId,
                  targetWishlistId: data.targetWishlistId,
                  ...(companyId && { companyId }),
              }
            : {
                  wishlistId: data.wishlistId,
                  productId: data.productId,
                  targetWishlistId: data.targetWishlistId,
                  ...(companyId && { companyId }),
              };

        return this.apiClient
            .post({
                url: "/moveItem",
                body: JSON.stringify(requestBody),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, MoveItemResponseSchema);
                }),
                map((result) => {
                    return result.movedItem;
                })
            );
    }

    public updateWishlist(data: UpdateWishlistRequestBody) {
        const companyId = this.customerInfoProvider.getCompany()?.id;
        const requestBody = {
            ...data,
            ...(companyId && { companyId }),
        };

        return this.apiClient
            .post({
                url: "/updateWishlist",
                body: JSON.stringify(requestBody),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, UpdateWishlistResponseSchema);
                }),
                map((result) => {
                    return result.updatedWishlist;
                })
            );
    }

    public createWishlist(data: { title: string }) {
        const companyId = this.customerInfoProvider.getCompany()?.id;
        const requestBody = {
            title: data.title,
            ...(companyId && { companyId }),
        };

        return this.apiClient
            .post({
                url: "/createWishlist",
                body: JSON.stringify(requestBody),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, CreateWishlistResponseSchema);
                }),
                map((result) => {
                    return result.createdWishlist;
                })
            );
    }

    public deleteWishlist(data: { wishlistId: number }) {
        const companyId = this.customerInfoProvider.getCompany()?.id;
        const params = {
            wishlistId: data.wishlistId,
            ...(companyId && { companyId }),
        };

        return this.apiClient
            .delete({
                url: "/deleteWishlist",
                queryParams: params,
            })
            .pipe(
                map((response) => {
                    return create(response.body, DeleteWishlistResponseSchema);
                }),
                map((result) => {
                    return result.deletedWishlist;
                })
            );
    }

    public duplicateWishlist(data: { wishlistId: number }) {
        return this.apiClient
            .post({
                url: "/duplicateWishlist",
                body: JSON.stringify({ wishlistId: data.wishlistId }),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        DuplicateWishlistResponseSchema
                    );
                }),
                map((result) => {
                    return result.wishlistsWithItems;
                })
            );
    }

    public getWishlistProductStatuses(data: { productIds: number[] }) {
        const companyId = this.customerInfoProvider.getCompany()?.id;
        const requestBody = {
            productIds: data.productIds,
            ...(companyId && { companyId }),
        };

        return this.apiClient
            .post({
                url: "/getWishlistProductStatuses",
                body: JSON.stringify(requestBody),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, ProductStatusesResponseSchema);
                }),
                map((result) => {
                    return result.results;
                })
            );
    }

    public getBackInStockWishlistItems(
        params: getBackInStockWishlistItemsParams
    ): Observable<WishlistsBackInStockItemEntity[]> {
        return this.apiClient
            .get({
                url: "/getBackInStockWishlistItems",
                queryParams: {
                    wishlistItems: JSON.stringify(params),
                },
                responseType: "json",
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        GetBackInStockItemsResponseSchema
                    );
                }),
                map((response) => {
                    return response.results;
                })
            );
    }

    public updateWishlistItemVariant(data: {
        wishlistId: number;
        productId: number;
        oldVariantId: number;
        newVariantId: number;
    }) {
        return this.apiClient
            .put({
                url: "/updateWishlistItemVariant",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        UpdateItemVariantResponseSchema
                    );
                }),
                map((result) => {
                    return result;
                })
            );
    }

    public updateWishlistItemQuantity(data: {
        wishlistId: number;
        productId: number;
        variantId: number;
        quantity: number;
    }) {
        return this.apiClient
            .put({
                url: "/updateWishlistItemQuantity",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(
                        response.body,
                        UpdateItemQuantityResponseSchema
                    );
                }),
                map((result) => {
                    return result;
                })
            );
    }

    public createDraftOrder(data: { wishlistId: number }) {
        return this.apiClient
            .post({
                url: "/createDraftOrder",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
                responseType: "text",
            })
            .pipe(
                map((response) => {
                    return response;
                })
            );
    }

    public addApprover(data: { wishlistId: number; approverId: number }) {
        return this.apiClient
            .post({
                url: "/addApprover",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, AddApproverResponseSchema);
                }),
                map((result) => {
                    return result;
                })
            );
    }

    public deleteApprover(data: { wishlistId: number; approverId: number }) {
        return this.apiClient
            .post({
                url: "/deleteApprover",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, DeleteApproverResponseSchema);
                }),
                map((result) => {
                    return result;
                })
            );
    }

    public approveWishlist(data: { wishlistId: number }) {
        return this.apiClient
            .post({
                url: "/approveWishlist",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) => {
                    return create(response.body, ApproveWishlistResponseSchema);
                }),
                map((response) => {
                    return response;
                })
            );
    }

    public sendNotificationToApprovers(data: { wishlistId: number }) {
        return this.apiClient
            .post({
                url: "/sendNotificationToApprovers",
                body: JSON.stringify(data),
                contentType: JsonMimeType,
                responseType: "text",
            })
            .pipe(
                map((response) => {
                    return response;
                })
            );
    }
}
